import React, { useMemo, type HTMLAttributes } from 'react';
import type { RequestOptions } from 'searchkit';
import c from 'classnames';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container, Text } from '@uncinc/uncinc-react-kitchen-sink';

import type {
  ParagraphContentOverview as Type,
} from '../../../../types/uncinc_profile/paragraphs';

import {
  constructQuery,
  constructAdditionalOverviewFilters,
} from '../../../../components/Search/MetaSearch/metaParagraphsHelpers';
import MetaSearch from '../../../../components/Search/MetaSearch';
import FaqOverview from './FaqOverview';

import { CONTENT_TYPE_FAQ } from '../../../../config/constants';

import './style.scss';

interface ParagraphContentOverviewProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const ParagraphContentOverview: React.FC<ParagraphContentOverviewProps> = ({
  id,
  entity,
}) => {
  const contentOverview = entity.toJS();
  const contentType = contentOverview.field_content_type[0].value;

  const title = contentOverview.field_title[0]?.value;
  const body = contentOverview.field_body[0]?.processed || '';

  const requestOptions: RequestOptions = {
    getBaseFilters: () => [
      constructQuery(contentType),
      constructAdditionalOverviewFilters(contentOverview),
    ],
  };

  // If no filter fields have more than one value,
  // it's no use showing facets.
  // TODO: Dynamic Facet options so we show only Facets that have
  // more than one option.
  const withFacets = useMemo(() => (
    contentOverview.field_labels.length > 1 || contentType === CONTENT_TYPE_FAQ
  ), [contentOverview, contentType]);

  const classNames = c(
    'Paragraph',
    'ParagraphMeta',
    'ParagraphContentOverview',
    `ParagraphContentOverview__${contentType}`,
  );

  let additionalParams = {};
  // Overwrite the result component so we can display the FAQ in an accordion overview.
  if (contentType === CONTENT_TYPE_FAQ) {
    additionalParams = {
      ResultListComponent: FaqOverview,
    };
  }

  return (
    <div id={id} className={classNames}>
      <Container>
        <div className="row">
          <div className="col-12">
            {(title || body) && (
              <div className="ParagraphContentOverview__body">
                <Text
                  title={title}
                  text={body}
                />
              </div>
            )}

            <MetaSearch
              requestOptions={requestOptions}
              contentType={contentType}
              hitsPerPage={9}
              withFacets={withFacets}
              withPagination
              {...additionalParams}
            />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ParagraphContentOverview;
